import { TTranslator } from '@smartshell/ui-kit';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import { TLocalesKeys } from 'localization';
import { ApplicationType, IShortcutTemplate } from 'types';

import { MAX_SCREENSHOTS } from './consts';
import {
  CommonFormTitlesKey,
  ICommonFormTitles,
  IFormValues,
} from './interfaces';

const getCommonFormTitles = (
  t: TTranslator<TLocalesKeys>
): Record<CommonFormTitlesKey, ICommonFormTitles> => {
  return {
    args: {
      title: t('application.args'),
      placeholder: t('application.args.placeholder'),
    },
    workingDirectory: {
      title: t('application.dir'),
      placeholder: t('application.dir.placeholder'),
    },
    accountsGroup: {
      title: t('application.account.format'),
      placeholder: t('application.account.placeholder'),
    },
    launchScript: {
      title: t('application.script.format'),
      placeholder: t('application.script.placeholder'),
    },
    password: {
      title: t('application.password.format'),
      placeholder: t('application.password.placeholder'),
    },
    icon_path: {
      title: t('application.path.format'),
      placeholder: t('application.path.placeholder'),
    },
  };
};

export const getFormTitlesConfig = (
  t: TTranslator<TLocalesKeys>
): Record<
  ApplicationType,
  Record<
    keyof Omit<
      IFormValues,
      | 'main_picture'
      | 'screenshots'
      | 'run_as_admin'
      | 'only_for_registered'
      | 'age_rating'
      | 'free_run'
      | 'template_id'
      | 'template_title'
    >,
    {
      title: string;
      placeholder: string;
      tooltipHeader?: string;
      tooltipContent?: string;
      info?: string;
    }
  >
> => {
  const COMMON_FORM_TITLES = getCommonFormTitles(t);

  return {
    GAME: {
      ...COMMON_FORM_TITLES,
      title: {
        title: t('application.game.title'),
        placeholder: t('application.game.title.placeholder'),
      },
      description: {
        title: t('application.description'),
        placeholder: t('application.description.placeholder'),
      },
      group: {
        title: t('application.game.group.title'),
        placeholder: t('application.game.group.placeholder'),
        info: t('application.game.group.info'),
      },
      path: {
        title: t('application.game.path'),
        placeholder: t('application.game.path.placeholder'),
        tooltipHeader: t('application.game.path.tooltipHeader'),
        tooltipContent: t('application.game.path.tooltipContent'),
      },
    },
    APPLICATION: {
      ...COMMON_FORM_TITLES,
      title: {
        title: t('application.app.title'),
        placeholder: t('application.app.title.placeholder'),
      },
      description: {
        title: '',
        placeholder: '',
      },
      group: {
        title: t('application.app.group.title'),
        placeholder: t('application.app.group.placeholder'),
        info: t('application.app.group.info'),
      },
      path: {
        title: t('application.app.path'),
        placeholder: t('application.app.path.placeholder'),
        tooltipHeader: t('application.app.path.tooltipHeader'),
        tooltipContent: t('application.app.path.tooltipContent'),
      },
    },
  };
};

export const setWorkingDirectoryPath = (
  firstGamePath: string,
  setValue: UseFormSetValue<IFormValues>
): void => {
  setValue('workingDirectory', firstGamePath.match(/.*\\/)?.[0] ?? '');
};

export const applyTemplateToForm = (
  template: IShortcutTemplate,
  getValues: UseFormGetValues<IFormValues>,
  setValue: UseFormSetValue<IFormValues>,
  mode: 'create' | 'update'
): void => {
  const current = getValues();

  if ((mode === 'create' || !current.title) && template.title) {
    setValue('title', template.title);
  }

  if (template.title) {
    setValue('template_title', template.title, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  }

  if ((mode === 'create' || !current.description) && template.description) {
    setValue('description', template.description, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  }

  if ((mode === 'create' || !current.args) && template.args) {
    setValue('args', template.args);
  }

  if ((mode === 'create' || !current.main_picture) && template.main_picture) {
    setValue('main_picture', template.main_picture);
  }

  if (template.screenshots?.length) {
    const currentScreenshots = current.screenshots?.value ?? [];

    if (mode === 'create') {
      setValue('screenshots.value', template?.screenshots);
    } else {
      if (currentScreenshots.length < MAX_SCREENSHOTS) {
        const remaining = MAX_SCREENSHOTS - currentScreenshots.length;

        const screenshotsFromTemplate = template.screenshots
          .filter((url) => !currentScreenshots.includes(url))
          .slice(0, remaining);

        if (screenshotsFromTemplate.length > 0) {
          setValue('screenshots.value', [
            ...currentScreenshots,
            ...screenshotsFromTemplate,
          ]);
        }
      }
    }
  }

  if (
    (mode === 'create' || current.run_as_admin === false) &&
    template.run_as_admin !== undefined
  ) {
    setValue('run_as_admin', template.run_as_admin);
  }

  if (
    (mode === 'create' || !current.age_rating) &&
    template.age_rating !== undefined
  ) {
    if ([0, 6, 12, 16, 18].includes(template.age_rating)) {
      setValue('only_for_registered', true);
      setValue('age_rating', template.age_rating);
    }
  }

  setValue('template_id', template.id);
};
